/**
 * G1 — Home.
 * 1:1 with assets/extracted/tier-1/G1-home.html.
 *
 * States:
 *  - default  — unsolved, today's CTA shows "Solve today" in rarity color
 *  - solved   — already solved today, CTA mutes to ink + Practice link shown
 *  - at-risk  — UTC reset < 2h away + streak alive, ring pulses around CTA
 *  - day-1    — no streak yet, streak row hidden
 *
 * M11 additions:
 *  - Live Pulse: 1px line at screen bottom, pulses every ~5s (silent visual)
 *  - H4 Streak Risk: Day 6/7 shows "X hours left · don't break it"
 */
import { generateDailyPuzzle, todaySeed } from '../../engine/generate';
import { regionName, t } from '../../i18n/runtime';
import { profile } from '../../state/profile';
import { fetchWeeklyCountry, getActiveNow } from '../../state/sync';
import { iconString } from '../icons';
import { el, html, on, raw } from '../html';
import type { RouteContext } from '../router';
import { tabbar } from '../tabbar';

/** Inline shield-badge SVG (v2.2 — was previously imported from m4-freeze which is cut). */
function freezeShieldBadge(count: number): string {
  const display = count > 1 ? `<span class="g1-shield-count">${count}</span>` : '';
  return `<svg width="14" height="16" viewBox="0 0 14 16" fill="none" aria-hidden="true">
    <path d="M7 1 L13 3 L13 8 C13 12 10 14.5 7 15 C4 14.5 1 12 1 8 L1 3 Z"
      fill="currentColor" stroke="rgba(26,26,26,0.25)" stroke-width="0.8" stroke-linejoin="round"/>
    <path d="M4.5 8 L6.5 10 L9.5 6" fill="none" stroke="#FAFAFA" stroke-width="1.3"
      stroke-linecap="round" stroke-linejoin="round"/>
  </svg>${display}`;
}

const RARITY_SYMBOL: Record<string, string> = {
  common: '●',
  uncommon: '◆',
  rare: '▲',
  epic: '★',
  legendary: '✦',
};
const RARITY_LABEL: Record<string, string> = {
  common: 'COMMON',
  uncommon: 'UNCOMMON',
  rare: 'RARE',
  epic: 'EPIC',
  legendary: 'LEGENDARY',
};

function flagEmoji(country: string): string {
  if (country.length !== 2) return '🏳️';
  const a = country.charCodeAt(0) - 65;
  const b = country.charCodeAt(1) - 65;
  if (a < 0 || a > 25 || b < 0 || b > 25) return '🏳️';
  return String.fromCodePoint(0x1f1e6 + a) + String.fromCodePoint(0x1f1e6 + b);
}

function isStreakAtRisk(lastSolveDate: string | null): boolean {
  if (!lastSolveDate) return false;
  const now = new Date();
  return 24 - now.getUTCHours() <= 2;
}

/**
 * Compute pulse intensity from active_now count.
 * intensity = clamp(log(count) / 8, 0, 1)
 * - count=1   → intensity 0
 * - count=10  → 0.288
 * - count=100 → 0.575
 * - count=1000 → 0.863
 * - count≥~3000 → 1.0
 */
export function livePulseIntensity(count: number): number {
  if (count <= 0) return 0;
  return Math.min(1, Math.log(count) / 8);
}

/** M11 — Live Pulse. Mounts a 1px line at screen bottom that pulses silently.
 *
 * P-2.1.4: intensity is driven by active_now count fetched from worker every 30s.
 * Offline / fetch failure → falls back to static sinusoidal at default intensity.
 */
function mountLivePulse(root: HTMLElement, reduceMotion: boolean): () => void {
  const bar = document.createElement('div');
  bar.className = 'live-pulse-bar';
  bar.setAttribute('aria-hidden', 'true');
  bar.style.cssText = `
    position:fixed;bottom:0;left:0;right:0;height:1px;
    background:var(--ink,#1A1A1A);opacity:0.12;
    pointer-events:none;z-index:1;
    transform-origin:center;
  `;
  root.appendChild(bar);
  if (reduceMotion) return () => bar.remove();

  // Current intensity — updated by polling loop; default 0 → uses 0.35 max as fallback.
  let intensity = 0;
  let frame = 0;
  let phase = 0;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  const PERIOD = 5000; // ~5s per pulse cycle

  // Async initial fetch — fire and forget; UI doesn't block.
  getActiveNow().then((count) => {
    intensity = livePulseIntensity(count);
  });

  // Poll every 30s to keep intensity fresh.
  pollTimer = setInterval(async () => {
    const count = await getActiveNow();
    intensity = livePulseIntensity(count);
  }, 30_000);

  let last = performance.now();
  const tick = (now: number) => {
    const dt = now - last;
    last = now;
    phase = (phase + dt / PERIOD) % 1;
    // Rest opacity = 0.12; peak amplitude scales with intensity.
    // When intensity=0 (offline): max opacity = 0.12 + 0.23 * 1 * peak = same as before (fallback).
    // When intensity=1 (dense): same amplitude 0.23, just as vivid.
    // The player perceives intensity via knowing the pulse = "others are here".
    const peak = Math.pow(Math.sin(phase * Math.PI), 8);
    const base = 0.08 + intensity * 0.04; // base shifts 0.08→0.12 with activity
    const alpha = base + 0.23 * peak;
    bar.style.opacity = String(alpha.toFixed(3));
    frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);
  return () => {
    if (pollTimer !== null) clearInterval(pollTimer);
    cancelAnimationFrame(frame);
    bar.remove();
  };
}

/** M11 — H4 Streak Risk: returns hours left in day. Shown on streak day 6 or 7. */
function hoursLeftToday(): number {
  const now = new Date();
  return 23 - now.getUTCHours();
}

/**
 * Streak Risk — the "felt need" trigger for the shield. Surfaces whenever the
 * player holds a streak worth protecting (≥ 3 days) and hasn't solved today, so
 * the shield offer appears at the moment of genuine loss-aversion (not buried in
 * Settings, not a permanent nag). Restrained per I-20: small ink text, no popup.
 */
const STREAK_RISK_MIN = 3;
function isStreakRisk(streak: number, alreadySolved: boolean): boolean {
  if (alreadySolved) return false;
  return streak >= STREAK_RISK_MIN;
}

export default function (ctx: RouteContext): { node: HTMLElement; cleanup: () => void } {
  const today = todaySeed();
  const p = profile().get();
  // GS-1 (hard invariant): every player sees the SAME global daily, deterministic
  // per UTC date. M6 novice daily-easing was removed — on RARE days it substituted
  // a non-global alt-seed puzzle here, violating GS-1 and diverging from the play
  // screen (home previewed an easier puzzle than the one actually played). Novice
  // onboarding is the welcome puzzle (FDT-O-001); difficulty ramp lives in Practice.
  const puzzle = generateDailyPuzzle(today);
  const alreadySolved = p.lastSolveDate === today;
  const stateAttr = alreadySolved
    ? 'solved'
    : p.streak === 0
      ? 'day-1'
      : isStreakAtRisk(p.lastSolveDate)
        ? 'at-risk'
        : 'default';
  // Solved today → the big CTA should drive the NEXT challenge (Practice unlocks
  // 24h unlimited after the daily), not a replay. Review is reachable from the
  // calendar (tap a gem) + the result screen, so it isn't a home primary.
  const ctaText = alreadySolved ? t('home.cta_continue') : t('home.cta_play');
  const ctaGlyph = iconString('play', 14);

  // Streak Risk — felt-need trigger: active streak (≥3) not yet solved today.
  // The shield offer rides this moment of loss-aversion. Restrained (I-20):
  // small ink text + one inline shield clause, no popup, no alarm color.
  const showRisk = isStreakRisk(p.streak, alreadySolved);
  const riskHours = hoursLeftToday();
  const riskUrgent = riskHours <= 3;
  const shieldGlyph = `<svg class="home-risk-shield-ic" width="12" height="12" viewBox="0 0 22 24" fill="none" aria-hidden="true"><path d="M11 2 L20 5 L20 12 C20 18 16 22 11 23 C6 22 2 18 2 12 L2 5 Z" fill="currentColor" stroke-width="0"/></svg>`;
  const riskShieldClause = p.freezeTokens > 0
    ? `<span class="home-risk-shield is-safe">${shieldGlyph}${t('g1.home.shieldSafe')}</span>`
    : `<button class="home-risk-shield" data-get-shield aria-label="${t('iap.shield_name')}">${shieldGlyph}${t('g1.home.shieldProtect')}</button>`;
  const h4Html = showRisk
    ? html`<div class="home-streak-risk" role="status" aria-live="polite">
        <span class="home-risk-line">${riskUrgent ? t('g1.home.streakRiskUrgent', { h: riskHours, n: p.streak }) : t('g1.home.streakRiskKeep', { n: p.streak })}</span>
        ${raw(riskShieldClause)}
      </div>`
    : '';

  const reduceMotion =
    p.a11y?.reduceMotion === 'on' ||
    (p.a11y?.reduceMotion !== 'off' && matchMedia('(prefers-reduced-motion: reduce)').matches);

  const root = el(html`
    <section class="screen-home" data-state="${stateAttr}" data-rarity="${puzzle.rarity}" aria-label="${t('home.today_label')}">
      <button class="home-hamburger" aria-label="${t('nav.menu')}" data-menu>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M3 6 H21 M3 12 H21 M3 18 H21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
      <div class="home-content">
        <div class="home-hero">
          <div class="home-eyebrow">${t('home.today_label')}</div>
          <div class="home-rarity-symbol rarity-${puzzle.rarity}" aria-hidden="true">${RARITY_SYMBOL[puzzle.rarity]}</div>
          <div class="home-rarity-label rarity-${puzzle.rarity}">${RARITY_LABEL[puzzle.rarity]}</div>
        </div>
        <div class="home-below">
          ${h4Html}
          <div class="home-cta-wrap">
            <span class="home-ring" aria-hidden="true"></span>
            <button class="home-cta" data-play aria-label="${ctaText}">
              <span class="home-cta-glyph" aria-hidden="true">${ctaGlyph}</span>
              <span>${ctaText}</span>
            </button>
          </div>
          ${!alreadySolved ? html`<button class="home-practice-link" data-practice aria-label="${t('home.cta_practice')}">${t('home.cta_practice')}</button>` : ''}
          ${p.streak > 0 ? html`<div class="home-streak${alreadySolved && !reduceMotion ? ' is-bumped' : ''}">${t('home.streak', { n: p.streak })}${p.freezeTokens > 0 ? raw(` <span class="home-freeze-badge" data-freeze-tooltip title="${t('g1.shield_tooltip')}">${freezeShieldBadge(p.freezeTokens)}</span>`) : (!showRisk ? raw(` <button class="home-shield-get" data-get-shield aria-label="${t('iap.shield_name')}" title="${t('iap.shield_tagline')}"><svg width="13" height="13" viewBox="0 0 22 24" fill="none" aria-hidden="true"><path d="M11 2 L20 5 L20 12 C20 18 16 22 11 23 C6 22 2 18 2 12 L2 5 Z" fill="currentColor" stroke-width="0"/><path d="M7 12 L10 15 L15 9" fill="none" stroke="var(--color-paper)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg><span class="home-shield-plus" aria-hidden="true">+</span></button>`) : '')}</div>` : ''}
          <div class="home-country" data-country-rank role="button" tabindex="0" aria-label="${t('nav.leaderboard')}">${t('home.country_pending', { flag: flagEmoji(p.country), country: regionName(p.country) })}</div>
          ${!alreadySolved ? html`<div class="home-country-sub">${t('home.country_sub')}</div>` : ''}
        </div>
      </div>
      <div data-tabbar></div>
    </section>
  `);
  // Apply rarity color via CSS variable on the host so .home-cta and other
  // rarity-tinted elements pick up the same hue per puzzle.
  root.style.setProperty('--rarity', `var(--rarity-${puzzle.rarity})`);

  const tb = root.querySelector('[data-tabbar]') as HTMLElement;
  tb.replaceWith(tabbar('home', (route) => ctx.navigate(route)));

  // Async: fetch real country rank and update the DOM element
  void (async () => {
    const rows = await fetchWeeklyCountry();
    const myRow = rows.find((r) => r.code === p.country);
    const rankEl = root.querySelector<HTMLElement>('[data-country-rank]');
    if (rankEl && myRow) {
      rankEl.textContent = t('home.country_rank', {
        flag: flagEmoji(p.country),
        country: regionName(p.country),
        rank: String(myRow.rank),
      });
    }
  })();

  on(root, 'click', '[data-play]', () => {
    // Solved → keep the player moving (Practice); unsolved → today's daily.
    if (alreadySolved) ctx.navigate('/practice');
    else ctx.navigate(`/play/${today}`);
  });
  on(root, 'click', '[data-practice]', () => ctx.navigate('/practice'));
  // IA (2026-06): Leaderboard left the tab bar — the country-rank line is now its
  // primary entry. Tapping it opens the country season leaderboard.
  on(root, 'click', '[data-country-rank]', () => ctx.navigate('/leaderboard'));
  on(root, 'click', '[data-menu]', () => ctx.navigate('/settings'));
  // v2.2: shield badge tap routes to /stats (shield inventory lives there
  // per MASTERPIECE-v2.2 §1.1; freeze-info standalone page is cut).
  on(root, 'click', '[data-freeze-tooltip]', (_e) => {
    ctx.navigate('/stats');
  });
  // When user has no shields yet, the "get shield" micro-button directly opens IAP sheet.
  on(root, 'click', '[data-get-shield]', () => ctx.navigate('/iap/buy'));

  // M11 — Live Pulse: 1px bottom bar with silent sinusoidal pulse
  const stopPulse = mountLivePulse(root, reduceMotion);

  return {
    node: root,
    cleanup: stopPulse,
  };
}
